import { useEffect, useRef } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

// No keystrokes, document content, or URLs are collected. All durations are measured
// on the client and validated again by FastAPI; they are not working-hours claims.
const INTERVAL_MS = Number(process.env.REACT_APP_ANALYTICS_BATCH_MS || 25000);
const IDLE_MS = Number(process.env.REACT_APP_ANALYTICS_IDLE_MS || 120000);
const RETRY_LIMIT = 1000;
const BATCH_SIZE = 60; // well below the keepalive payload limit
const uuid = () => crypto.randomUUID();
const tabId = () => {
  const key = "smokyq-analytics-tab-id";
  try {
    let value = sessionStorage.getItem(key);
    if (!value) { value = uuid(); sessionStorage.setItem(key, value); }
    return value;
  } catch {
    // Storage may be disabled; tracking must never prevent the app rendering.
    return uuid();
  }
};

/** Content-free, batched browser engagement. Queue survives a failed page-exit send
 *  within the same tab. Event IDs remain unchanged on retries (server UPSERT dedupe). */
export default function useEngagementTracking({ userId, projectId, module }) {
  const scopeRef = useRef({ projectId, module });
  const recordRef = useRef(() => {});

  useEffect(() => {
    if (!userId) return undefined;
    const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
    const queueKey = `smokyq-analytics-queue-${userId}`;
    const sessionId = uuid();
    const browserTabId = tabId();
    let pending;
    try { pending = JSON.parse(sessionStorage.getItem(queueKey) || "[]"); }
    catch { pending = []; }
    if (!Array.isArray(pending)) pending = [];
    let token = null;
    let sending = false;
    let consecutiveFailures = 0;
    let nextAttemptAt = 0;
    let alive = true;
    let started = document.visibilityState === "visible" ? Date.now() : null;
    let activeUntil = 0;
    // Visibility alone is NOT an interaction signal. Reading remains measured
    // as visible time, not automatically credited to interaction-active time.
    let activeWindows = [];
    let lastInteraction = 0;

    const persist = () => {
      try { sessionStorage.setItem(queueKey, JSON.stringify(pending.slice(-RETRY_LIMIT))); }
      catch { /* storage disabled; in-memory retries still work */ }
    };
    const refresh = () => fetchAuthSession().then(auth => {
      if (alive) token = auth.tokens?.accessToken?.toString() || null;
    }).catch(() => { token = null; });
    void refresh();
    const authTimer = setInterval(refresh, 4 * 60 * 1000);

    const flush = () => {
      if (sending || !token || pending.length === 0 || Date.now() < nextAttemptAt) return;
      const batch = pending.slice(0, BATCH_SIZE);
      sending = true;
      let succeeded = false;
      // fetch + keepalive supports Authorization; sendBeacon cannot supply a Bearer header.
      fetch(`${base}/analytics/engagement/batch`, {
        method: "POST", keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ events: batch }),
      }).then(response => {
        if (!response.ok) throw Error(`Engagement HTTP ${response.status}`);
        succeeded = true;
        consecutiveFailures = 0;
        nextAttemptAt = 0;
        const ids = new Set(batch.map(event => event.event_id));
        pending = pending.filter(event => !ids.has(event.event_id));
        persist();
      }).catch(() => {
        // Preserve the batch; retry through the normal timer with bounded backoff.
        // A missing route or outage must not create an immediate retry loop.
        consecutiveFailures = Math.min(consecutiveFailures + 1, 5);
        nextAttemptAt = Date.now() + Math.min(300000, 30000 * (2 ** (consecutiveFailures - 1)));
        persist();
      }).finally(() => {
        sending = false;
        if (alive && succeeded && pending.length > 0) flush();
      });
    };
    const append = (from, until, active) => {
      if (until - from < 100 || !scopeRef.current.module) return;
      const duration = (until - from) / 1000;
      pending.push({
        event_id: uuid(), session_id: sessionId, browser_tab_id: browserTabId,
        project_id: scopeRef.current.projectId || null, module: scopeRef.current.module,
        started_at: new Date(from).toISOString(), ended_at: new Date(until).toISOString(),
        visible_seconds: duration, active_seconds: active ? duration : 0,
        idle_seconds: active ? 0 : duration,
      });
      if (pending.length > RETRY_LIMIT) {
        // Never silently replace newer events; older undeliverable events have a
        // bounded retention to avoid unbounded sessionStorage growth.
        pending = pending.slice(-RETRY_LIMIT);
      }
      persist();
    };
    const record = () => {
      const end = Date.now();
      if (started == null || end <= started) return;
      // Split at every idle boundary. Each stored segment is entirely active or
      // entirely idle; clipped/overlapping-tab rollups remain mathematically valid.
      const windows = activeWindows.slice();
      let cursor = started;
      for (const [rawStart, rawEnd] of windows) {
        const from = Math.max(cursor, rawStart);
        const until = Math.min(end, rawEnd);
        if (from > cursor) append(cursor, from, false);
        if (until > from) { append(from, until, true); cursor = until; }
      }
      if (end > cursor) append(cursor, end, false);
      started = document.visibilityState === "visible" ? end : null;
      activeWindows = activeUntil > end ? [[end, activeUntil]] : [];
      if (pending.length >= BATCH_SIZE) flush();
    };
    recordRef.current = record;
    const interact = () => {
      const now = Date.now();
      if (now - lastInteraction < 1000 || started == null) return;
      lastInteraction = now;
      if (activeWindows.length && activeWindows[activeWindows.length - 1][1] >= now) {
        activeWindows[activeWindows.length - 1][1] = now + IDLE_MS;
      } else activeWindows.push([now, now + IDLE_MS]);
      activeUntil = now + IDLE_MS;
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") { record(); started = null; flush(); }
      else {
        started = Date.now(); activeUntil = 0;
        activeWindows = [];
        flush();
      }
    };
    const exit = () => { record(); flush(); persist(); };
    const timer = setInterval(() => { record(); flush(); }, Math.max(15000, Math.min(30000, INTERVAL_MS)));
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("pagehide", exit);
    window.addEventListener("pointerdown", interact, { passive: true });
    window.addEventListener("keydown", interact, { passive: true });
    window.addEventListener("scroll", interact, { passive: true });
    return () => {
      record(); alive = false; flush(); persist();
      clearInterval(timer); clearInterval(authTimer);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("pagehide", exit);
      window.removeEventListener("pointerdown", interact);
      window.removeEventListener("keydown", interact);
      window.removeEventListener("scroll", interact);
      recordRef.current = () => {};
    };
  }, [userId]);

  useEffect(() => {
    recordRef.current(); // close the old module/project BEFORE changing scope
    scopeRef.current = { projectId, module };
  }, [userId, projectId, module]);
}
